import isNil from 'lodash/isNil';
import numberUtils from './numberUtils';
import dateUtils from './../utils/dateUtils';
import { pluralize } from './stringUtils';

import type Comparables from '../models/Comparables';

type PetsType = {
    cats: boolean;
    largeDogs: boolean;
    smallDogs: boolean;
};

type LaundryType = {
    inUnit: boolean;
    shared: boolean;
};

type ParkingType = {
    valet: boolean;
    garage: boolean;
    offStreet: boolean;
};

type HistoryType = {
    lastUpdated: {
        date: number;
        ago: string;
    };
    deactivated?: {
        date: number;
        ago: string;
    };
};

export const parser = {
    getStatus: (isActive: boolean, history: HistoryType, units: number): string | null => {
        if (isNil(isActive) || isNil(history)) {
            return null;
        }

        if (isActive) {
            /**
             * return "Active __ days/hours/minutes"
             *
             * example `ago` strings:
             *  - "59 minutes, 15 seconds ago"
             *  - "7 days ago"
             *  - "14 hours, 29 minutes ago"
             */
            if (history.lastUpdated && history.lastUpdated.ago) {
                if (units > 1) {
                    return `${units} units available`;
                }
                return `Active ${history.lastUpdated.ago.split('ago')[0].split(',')[0].trim()}`;
            }
        } else {
            /**
             * return "Last seen {date}"
             *
             * example `date`:
             *  - 1586317192000
             *  - 1567809147000
             */
            if (history.deactivated && history.deactivated.date) {
                try {
                    return 'Last seen ' + dateUtils.formatMonthStrDayStr(new Date(history.deactivated.date));
                } catch {
                    return 'Inactive';
                }
            }
        }

        return 'Inactive';
    },
    getLongestAddress: (comparables: Array<Comparables>) => {
        return comparables
            .map((comp: Comparables) => comp.street || '')
            .reduce((addressA, addressB) => (addressA.length > addressB.length ? addressA : addressB));
    },
    ac: (hasAc: boolean): string => {
        if (hasAc) {
            return 'Has A/C';
        }

        return 'No A/C';
    },
    heat: (hasHeating: boolean): string => {
        if (hasHeating) {
            return 'Has heating';
        }

        return 'No heating';
    },
    laundry: (laundryObject: LaundryType): string => {
        const { inUnit, shared } = laundryObject;

        if (inUnit) {
            return 'In-unit laundry';
        }

        if (shared) {
            return 'Shared laundry';
        }

        return 'No laundry';
    },
    parking: (parkingObject: ParkingType): string => {
        const { valet, garage, offStreet } = parkingObject;

        if (valet) {
            return 'Valet parking';
        }

        if (garage) {
            return 'Garage parking';
        }

        if (offStreet) {
            return 'Off-street parking';
        }

        return 'No parking';
    },
    pets: (petsObject: PetsType): string => {
        const { cats, smallDogs, largeDogs } = petsObject;
        const catsOnly = cats && !largeDogs && !smallDogs;
        const dogsOnly = !cats && largeDogs && smallDogs;
        const largeDogsOnly = !cats && largeDogs && !smallDogs;
        const smallDogsOnly = !cats && !largeDogs && smallDogs;
        const catsAndLargeDogsOnly = cats && largeDogs && !smallDogs;
        const catsAndSmallDogsOnly = cats && !largeDogs && smallDogs;
        const catsAndDogs = cats && largeDogs && smallDogs;

        if (catsOnly) {
            return 'Cats only';
        }

        if (dogsOnly) {
            return 'Dogs only';
        }

        if (largeDogsOnly) {
            return 'Large dogs only';
        }

        if (smallDogsOnly) {
            return 'Small dogs only';
        }

        if (catsAndLargeDogsOnly) {
            return 'Cats & large dogs only';
        }

        if (catsAndSmallDogsOnly) {
            return 'Cats & small dogs';
        }

        if (catsAndDogs) {
            return 'Cats & dogs';
        }

        return 'No pets';
    },
    sqft: (minSqft: number, maxSqft: number): string => {
        const low = numberUtils.compact(minSqft);
        const high = numberUtils.compact(maxSqft);

        if (minSqft !== maxSqft) {
            return `${low}-${high}`;
        }

        return low;
    },
    monthlyRent: (lowPrice: number, highPrice: number): string => {
        const low = numberUtils.asCurrency(lowPrice);
        const high = numberUtils.asCurrency(highPrice);
        if (lowPrice !== highPrice) {
            return `${low}-${high}`;
        }

        return low;
    },
    pricePerSqft: (lowPrice: number, highPrice: number, minSqft: number): string | null => {
        if (lowPrice < 1 || highPrice < 1) {
            return null;
        }

        if (minSqft && lowPrice !== highPrice) {
            return `$${numberUtils.trimDecimal(lowPrice / minSqft, 2)}+`;
        }

        return `$${(Math.round((lowPrice / minSqft) * 100) / 100).toFixed(2)}`;
    },
    bubbleWindow: {
        monthlyRent: (lowPrice: number, highPrice: number): string => {
            const low = numberUtils.asCurrency(lowPrice);
            if (lowPrice !== highPrice) {
                return `${low}+`;
            }

            return low;
        }
    },
    beds: (beds: number) => {
        return beds === 0 ? 'Studio' : pluralize('bed', beds, true);
    }
};
